<script>
import AdminPanel from '@components/admin-panel/admin-panel.vue';
import CardProspect from '@components/cards/card-prospect.vue';
import CardProspectingProgress from '@components/cards/card-prospecting-progress.vue';
import CardProspectPlaceholder from '@components/cards/card-prospect-placeholder.vue';
import Layout from '@layouts/main.vue';
import ProspectClientAcquisitionSteps from '@components/prospects/prospect-client-acquisition-steps.vue';
import ProspectContactForm from '@components/forms/prospect-contact-form.vue';
import ProspectMessageComposer from '@components/prospects/prospect-message-composer.vue';
import SearchInput from '@components/search-input/search-input.vue';
import SplitPaneFull from '@components/panels/split-pane-full.vue';
import SubheaderActions from '@components/subheader/subheader-actions.vue';
import { adminPanelMixin } from '@mixins/admin-panel-mixin';
import { breakpointsMixin } from '@mixins/breakpoints-mixin.js';
import {
  adminPanelComputed,
  adminPanelMethods,
  alertComputed,
  alertMethods,
  authComputed,
  campaignComputed,
  campaignMethods,
  modalComputed,
  modalMethods,
  prospectComputed,
  prospectMethods,
} from '@state/helpers';
const AlertBasic = () =>
  import(
    /* webpackChunkName: "AlertBasic" */ '@components/notifications/alert-basic.vue'
  );
const UseLargeScreen = () =>
  import(
    /* webpackChunkName: "UseLargeScreen" */ '@components/use-large-screen.vue'
  );
const ProspectConfirmationModal = () =>
  import(
    /* webpackChunkName: "ConfirmationModal" */ '@components/modals/prospect-confirmation-modal.vue'
  );
export default {
  page: {
    title: 'Prospects',
    meta: [{ name: 'description', content: 'The Prospects page.' }],
  },
  mixins: [adminPanelMixin, breakpointsMixin],
  components: {
    AdminPanel,
    CardProspect,
    CardProspectingProgress,
    CardProspectPlaceholder,
    Layout,
    ProspectClientAcquisitionSteps,
    ProspectContactForm,
    ProspectMessageComposer,
    SplitPaneFull,
    SearchInput,
    SubheaderActions,
    ProspectConfirmationModal,
    UseLargeScreen,
    AlertBasic,
  },
  data() {
    return {
      alertTimeoutId: null,
      prospectsList: null,
      removeAdminPanelPadding: false,
      searchComponentKey: 0,
      searchOpts: { keys: ['label'] },
      filterOpts: ['All Leads', 'Active Leads', 'Archived Leads'],
      defaultOpt: 'All Leads',
      selectedOpt: 'All Leads',
    };
  },
  computed: {
    ...authComputed,
    ...adminPanelComputed,
    ...alertComputed,
    ...campaignComputed,
    ...modalComputed,
    ...prospectComputed,
    prospects() {
      const filter = {
        'All Leads': () => this.allProspects,
        'Active Leads': () =>
          this.allProspects.filter(
            (prospect) => !('deleted_at' in prospect) && !prospect.is_converted
          ),
        'Archived Leads': () =>
          this.allProspects.filter((prospect) => 'deleted_at' in prospect),
      };
      return filter[this.selectedOpt]();
    },
    placeholderArr() {
      // max of 5 placeholders when no prospects
      const placeholders = [
        { id: 'ph-One' },
        { id: 'ph-Two' },
        { id: 'ph-Three' },
        { id: 'ph-Four' },
        { id: 'ph-Five' },
      ];
      if (
        this.allProspects &&
        this.allProspects.length < 5 &&
        this.selectedOpt === 'All Contacts'
      ) {
        return placeholders.slice(this.allProspects.length);
      } else {
        return [];
      }
    },
  },
  mounted() {
    // onMount clear continueRoute if it exsits from
    // discarding prospect changes on route change
    if (this.continueRoute) {
      this.setContinueRoute(null);
    }
  },
  methods: {
    ...adminPanelMethods,
    ...alertMethods,
    ...campaignMethods,
    ...modalMethods,
    ...prospectMethods,
    visibilityChanged(isVisible, entry) {
      this.searchComponentKey++;
    },
    onFilterResults() {},
    async showAdminPanel(component, data = null, size = '') {
      await this.setPanelContent(this.getPanelContent(component, data, size));
      this.setShowAdminPanel();
    },
    showProspectMsgComposer() {
      const data = {
        showResetButton: true,
      };
      this.removeAdminPanelPadding = true;
      this.setSelectedCampaign(this.campaigns?.[0]);
      this.showAdminPanel('ProspectMessageComposer', data, 'lg');
    },
    onResetAdminPanel() {
      this.setOverriddenCampaignTemplate(null);
      this.setResetOverriddenMultiCampaignTemplate();
    },
    onComposeSingleMsg(type) {
      const data = {
        panelTitle: `Compose ${type} to ${this.selectedProspectFullName}`,
        msgType: type.toLowerCase(),
        showResetButton: true,
      };
      this.removeAdminPanelPadding = false;
      this.showAdminPanel('ProspectContactForm', data);
    },
    onHidePanel() {
      if (
        this.overriddenCampaignTemplate ||
        this.overriddenCampaignSmsTemplate ||
        this.overriddenCampaignEmailTemplate
      ) {
        this.$bvModal.show('unsaved-changes-modal');
      }
    },
    async toClient() {
      if (this.convertedClient) {
        await this.$router
          .push({
            name: 'clientProgram',
            params: {
              id: this.convertedClient._id,
              client: this.convertedClient,
            },
          })
          .catch(() => {});
        this.setConvertedClient(null);
      }
    },
    resetTimeout() {
      if (this.alertTimeoutId) {
        clearTimeout(this.alertTimeoutId);
      }
    },
  },
  watch: {
    showAlert(newVal, oldVal) {
      if (newVal) {
        this.alertTimeoutId = setTimeout(() => {
          this.setShowAlert();
        }, 8000);
      } else {
        this.resetTimeout();
      }
    },
  },
};
</script>

<template>
  <UseLargeScreen v-if="isSmallScreenView" />
  <Layout v-else>
    <AlertBasic :hideCloseBtn="true">
      <span class="alert-msg" v-if="alertText">
        {{ alertText }}
      </span>
    </AlertBasic>
    <AlertBasic v-if="convertedClient" @dismiss="setConvertedClient(null)">
      <div class="avatar"></div>
      <div class="alert-details">
        <span class="congrats">Congratulations!</span>
        <span class="alert-msg">
          {{
            convertedClient.profile &&
              convertedClient.profile.firstName +
                ' ' +
                convertedClient.profile.lastName
          }}
          is now a client. Check out their
          <a @click="toClient"> Client Profile</a>
        </span>
      </div>
    </AlertBasic>
    <ProspectConfirmationModal />
    <AdminPanel
      :title="panelContent ? panelContent.title : ''"
      :size="panelContent ? panelContent.size : ''"
      :noPad="removeAdminPanelPadding"
      @reset-admin-panel="onResetAdminPanel"
      @hide-panel="onHidePanel"
    >
      <component
        v-if="panelContent"
        :is="panelContent.component"
        :content="panelContent"
      />
    </AdminPanel>
    <div
      class="container-wrapper dark"
      v-observe-visibility="visibilityChanged"
    >
      <div class="content-wrapper">
        <SubheaderActions>
          <SearchInput
            :key="searchComponentKey"
            :list="prospectsList"
            :options="searchOpts"
            @filter-results="onFilterResults"
          />
          <div class="sort-wrapper">
            <span class="sort-by-label">Filter:</span>
            <v-select
              :options="filterOpts"
              :searchable="false"
              :clearable="false"
              v-model="defaultOpt"
              @input="selectedOpt = $event"
            />
          </div>
          <BaseButton
            type="button"
            class="btn-primary btn-round"
            :disabled="!prospects.length"
            @click="showProspectMsgComposer"
          >
            <BaseIcon name="envelope" />
            Start Campaign
          </BaseButton>
        </SubheaderActions>
        <div class="row">
          <div class="col-8 col-prospects">
            <div class="row row-page-title">
              <h2 class="prospect-section-title"
                >Looking for your first lead or 5?</h2
              >
            </div>
            <div class="row client-steps">
              <ProspectClientAcquisitionSteps
                @send-invite="showProspectMsgComposer"
              />
            </div>

            <div class="row row-page-title">
              <h5 class="prospect-section-title">Leads</h5>
            </div>
            <div class="row row-prospect-cards">
              <CardProspectPlaceholder :isButton="true" />
              <template v-for="(prospect, i) in prospects">
                <CardProspect
                  :class="{
                    'archived-overlay':
                      prospect.deleted_at && selectedOpt === 'All Contacts',
                  }"
                  :prospect="prospect"
                  :key="i"
                  @compose-single-msg="onComposeSingleMsg"
                />
              </template>
              <template v-show="placeholderArr.length">
                <CardProspectPlaceholder
                  v-for="placeholder in placeholderArr"
                  :placeholderId="placeholder.id"
                  :key="placeholder.id"
                />
              </template>
            </div>
          </div>
          <div class="col">
            <CardProspectingProgress />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.prospect-section-title {
  margin-bottom: 2.4rem;
}

.col-prospects {
  padding-right: 4rem;
}

.row {
  &.row-page-title,
  &.row-prospect-cards,
  &.client-steps {
    padding: 0 1.5rem;
  }
}

.alert-details {
  display: flex;
  flex-direction: column;
  margin-left: 2rem;

  .congrats {
    font-size: 1.8rem;
    font-weight: bold;
    line-height: 23px;
  }
  .alert-msg {
    font-size: 1.8rem;
    font-weight: 400;
    line-height: 23px;
  }
}
.avatar {
  align-items: center;
  background-color: $color-light-gray-five;
  border-radius: $base-border-radius-md;
  display: flex;
  height: 7.2rem;
  justify-content: center;
  margin-right: 1.3rem;
  width: 7.2rem;

  .icon-plus {
    font-size: 1.4rem;
  }
}
::v-deep {
  .btn-primary .fa-envelope {
    margin-right: 0.6rem;
  }

  .action-footer-wrapper {
    padding-right: 4rem;
  }
}
.sort-wrapper {
  align-items: center;
  display: flex;
  width: 30%;
  @media (max-width: 900px) {
    width: 40%;
  }
  @media (max-width: 750px) {
    width: 50%;
  }
  .sort-by-label {
    @extend %label;

    margin-bottom: 0;
    padding-right: 1rem;
    width: 9rem;
  }

  .v-select {
    width: 100%;

    ::v-deep .vs__dropdown-toggle {
      background-color: white;
      margin-bottom: 0 !important;
      .vs__selected-options {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        span {
          white-space: nowrap;
        }
      }
    }
  }
}
</style>
